import { defineStore } from 'pinia';
import { reactive } from 'vue';
import $fetch from '~/modules/fetch';
import { useTracker } from '~/composables/tracker';
import { requirejs } from '~/modules/require';

// VARIABLES
const DB_NAME = 'Make';
const DB_VERSION = 14;
const SCREENSHOTS = 'screenshots';
const FILES = 'file';

const tracker = useTracker();

// METHODS
const getKey = (payload: { type?: any }) => (payload?.type === SCREENSHOTS ? SCREENSHOTS : FILES);
const revokeThumbnailUrl = (thumbnails: any, id: string | number) => {
  if (thumbnails[id]?.image) {
    URL.revokeObjectURL(thumbnails[id].image as string);
  }
};

const thumbnailWithUrl = (thumbnail: { id: string | number; image: Blob | MediaSource }) => {
  revokeThumbnailUrl(thumbnail, thumbnail.id);
  return { ...thumbnail, image: URL.createObjectURL(thumbnail.image) };
};

export const useFileStore = defineStore({
  id: 'file',
  state: () => ({
    db: null,
    thumbnails: reactive({}),
    files3D: [],
  }),
  getters: {
    files3DInRAM(s) {
      return s.files3D;
    },
  },
  actions: {
    async isThumbnailExistsInDB(payload: any) {
      const thumbnails: any = await this.getThumbnails();

      return thumbnails.some((t: { id: any }) => t.id === payload.id);
    },
    async isFileExistsInDB(payload: any) {
      const files: any = await this.getFiles(null);

      return files.some((f: { name: any }) => f.name === payload.name);
    },
    createDB() {
      const database = this.db;
      if (database?.objectStoreNames.contains(FILES)) {
        database?.deleteObjectStore(FILES);
      }

      if (database?.objectStoreNames.contains(SCREENSHOTS)) {
        database?.deleteObjectStore(SCREENSHOTS);
      }

      database?.createObjectStore(FILES, { autoIncrement: true, keyPath: 'name' });
      database?.createObjectStore(SCREENSHOTS, { autoIncrement: true, keyPath: 'id' });
    },
    async getDB() {
      if (this.db && Object.keys(this.db).length > 0) {
        return this.db;
      }

      return new Promise((resolve, reject) => {
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = (e) => {
          reject(new Error(e));
        };

        request.onsuccess = (e) => {
          this.db = e.target.result;
          resolve(this.db);
        };

        request.onupgradeneeded = (e) => {
          this.db = e.target.result;
          this.createDB(); // Appel de l'action de création de structure
        };
      });
    },
    async uploadFilesToFCSWith3DDriveFallback(files: any[], onProgressCb?: undefined) {
      try {
        const fcsFiles = await this.uploadFilesToFCS(files, onProgressCb);
        return fcsFiles;
      } catch (error) {
        console.error('Failed uploading with FCS, starting to fallback to 3DDrive\n', error);
        const [MP3DPDriveUtils] = await requirejs<[any]>([
          'DS/MP3DPApplicationStore/MP3DPDriveUtils',
        ]);
        return Promise.all(files.map((file: any) => MP3DPDriveUtils.uploadFile(file)));
      }
    },
    async uploadFilesToFCS(
      files: any[],
      onProgressCb?:
        ((arg0: { name: any; progress: number; fileProgress?: number }) => void) | undefined,
    ) {
      const uploadedFiles: any[] = [];
      const totalSize = files.reduce((tot: number, curr: { size: number }) => tot + curr.size, 0);
      const currentSize: Record<string, number> = {};

      const calculateProgress = () => {
        const uploadedSize: number = Object.values(currentSize).reduce(
          (acc: number, cur) => acc + cur,
          0,
        );
        return (uploadedSize / totalSize) * 100;
      };

      await Promise.all(
        files.map(async (f: Blob) => {
          const formData = new FormData();
          formData.append('file', f, f.name);

          try {
            onProgressCb?.({ name: f.name, progress: 0 });

            const filesObj = await $fetch.post('/resources/mdcart/documents/3dpart', {
              query: { service: '3dprinting' },
              form: formData,
              timeout: 360000,
              onUploadProgress(e: { loaded: number }) {
                currentSize[f.name] = e.loaded;
                onProgressCb?.({
                  name: f.name,
                  progress: Math.min(calculateProgress(), 100),
                  fileProgress: (e.loaded / f.size) * 100,
                });
              },
            });

            Object.entries(filesObj).forEach(([filename, id]) => {
              uploadedFiles.push({
                id,
                title: filename,
                extension: filename.split('.').pop(),
                tenant: window.ds?.tenant,
              });
            });
          } catch (error) {
            void tracker?.error(
              'ordering',
              'HIGH',
              error ? error.message : 'unknown',
              'fcs_upload_error',
              [f.name, f.type],
              [f.size],
            );

            throw new Error(`Error when uploading file : ${f.name}: ${error.message}`);
          }
        }),
      );

      return uploadedFiles;
    },
    async writeFileInDB(payload: any) {
      let alreadyExists = false;

      if (payload.type === SCREENSHOTS) {
        this.thumbnails[payload.id] = thumbnailWithUrl(payload);

        alreadyExists = await this.isThumbnailExistsInDB(payload);
      } else {
        alreadyExists = await this.isFileExistsInDB(payload);
      }

      await this.getDB();

      return new Promise((resolve) => {
        const key = getKey(payload);
        const trans = this.db?.transaction([key], 'readwrite');
        const store = trans.objectStore(key);

        const writeFileToStore = () => {
          store.add(payload);
          trans.oncomplete = () => {
            resolve(payload);
          };
        };

        if (alreadyExists && payload.overwrite) {
          const request = store.delete(payload.id);
          request.onsuccess = writeFileToStore;
        } else if (alreadyExists) {
          resolve(payload);
        } else {
          writeFileToStore();
        }
      });
    },
    async writeFilesInDB(payload = []) {
      return Promise.all(payload.map(async (file) => this.writeFileInDB(file)));
    },
    async fetchThumbnails(payload: { cart?: any }) {
      const cartIds = (payload.cart.Items || payload.cart.getCartItems('MP3DP_PrintRequest')).map(
        (it: { id: any; get: (arg0: string) => any }) => it.id || it.get('id'),
      );

      if (!cartIds.length) {
        return;
      }

      try {
        const thumbnails: any = await $fetch.get('/resources/mdcart/cart-items/thumbnail', {
          query: { itemPids: cartIds, service: '3dprinting' },
        });

        if (!thumbnails.items.length) {
          return {};
        }

        return thumbnails.items.reduce(
          (acc: Record<string, any>, thumb: { id: string | number; docUrl: any }) => {
            acc[thumb.id] = thumb.docUrl;
            return acc;
          },
          {},
        );
      } catch (error) {
        console.error('thumbnail fetching error', error);
        return {};
      }
    },
    async getNumFiles(payload: { type?: any } | undefined) {
      return new Promise((resolve, reject) => {
        const key = getKey(payload);
        if (!this.db?.transaction) {
          resolve(null);
        }

        const trans = this.db?.transaction([key], 'readonly');
        const store = trans.objectStore(key);
        const countRequest = store.count();
        countRequest.onsuccess = () => {
          resolve(countRequest.result);
          countRequest.onsuccess = () => {
            resolve(countRequest.result);
          };

          countRequest.onerror = () => {
            reject(countRequest.error);
          };
        };
      });
    },
    async getFileNames(payload: { type?: any } | undefined) {
      return new Promise((resolve, reject) => {
        const key = getKey(payload);
        if (!this.db?.transaction) {
          resolve(null);
        }

        const trans = this.db?.transaction([key], 'readonly');
        const store = trans.objectStore(key);
        const fileNamesRequest = store.getAllKeys();

        fileNamesRequest.onsuccess = () => {
          resolve(fileNamesRequest.result);
          fileNamesRequest.onsuccess = () => {
            resolve(fileNamesRequest.result);
          };

          fileNamesRequest.onerror = () => {
            reject(fileNamesRequest.error);
          };
        };
      });
    },
    storeFileInRAM(file: any) {
      this.files3D.push(file);
    },
    storeFilesInRAM(files: any[]) {
      // Append: each upload adds to the RAM files (uploading a 2nd file must keep the 1st).
      // Use clearFilesInRAM() to empty the RAM store (e.g. on cart reset).
      files.forEach((f) => this.files3D.push(f));
    },
    clearFilesInRAM() {
      this.files3D = [];
    },
    async getFilesFromAllStorages(_p0?: { root: boolean }) {
      const ramFiles = this.files3D;
      const indexedDbFiles = ramFiles.length ? [] : ((await this.getFiles(undefined)) as any[]);

      return [...indexedDbFiles, ...ramFiles].filter(Boolean);
    },
    async getFiles(payload: { type: any } | undefined) {
      await this.getDB();
      return new Promise((resolve) => {
        const key = getKey(payload);
        const files: any = [];
        const trans = this.db?.transaction([key], 'readonly');
        trans.oncomplete = () => {
          if (payload && payload.type === SCREENSHOTS) {
            // No decryption needed
            if (!this.thumbnails || !Object.keys(this.thumbnails).length) {
              this.thumbnails = files.reduce(
                (acc: Record<string, any>, thumb: { id: string | number }) => {
                  acc[thumb.id] = thumbnailWithUrl(thumb as any);
                  return acc;
                },
                {},
              );
            }

            resolve(files);
            return;
          }

          const decryptedFiles = files.reduce(
            (acc: File[], file: { data: string; name: string }) => {
              // Already in the decrypted format
              if (file instanceof File) {
                acc.push(file);
                return acc;
              }

              const binary = atob(file.data.split(',')[1]);
              const array = new Uint8Array(binary.length);
              for (let i = 0; i < binary.length; i += 1) {
                array[i] = binary.charCodeAt(i);
              }

              acc.push(new File([array], file.name));
              return acc;
            },
            [],
          );
          resolve(decryptedFiles);
        };

        const store = trans.objectStore(key);
        store.openCursor().onsuccess = (e: { target: { result: any } }) => {
          const cursor = e.target.result;
          if (cursor) {
            files.push(cursor.value);
            cursor.continue();
          }
        };
      });
    },
    /**
     * Saves a 3D printing part thumbnail in the database.
     * @param {Object} payload - The data needed to save the thumbnail.
     * @param {Blob} payload.image - The image to be saved as a Blob.
     * @param {string} payload.id - The identifier associated with the thumbnail.
     */
    async saveThumbnailInDb(payload: Record<string, unknown> | undefined) {
      return this.writeFileInDB({ ...payload, type: SCREENSHOTS, overwrite: true });
    },
    /** Get screenshots of 3D Parts, Thumbnails objects have a Blob image, use the thumbnails getter
     * if you want to use the object link directly
     */
    async getThumbnails() {
      return this.getFiles({ type: SCREENSHOTS });
    },
    async removeThumbnail(id: string) {
      return this.removeFile({ type: SCREENSHOTS, id });
    },
    /** Clear all thumbnails from both indexedDB and cache */
    async removeThumbnails() {
      return this.removeFiles({ type: SCREENSHOTS });
    },
    async getFile(payload: any) {
      // Optimization opportunity: avoid calling getFiles here.
      const files = (await this.getFiles(undefined)) as any[];
      const file = files.find(
        (el: { partId: any; name: any }) => el.partId === payload || el.name === payload,
      );
      if (!file) {
        console.error(`Could not find file to get ${payload}`);
      }

      return file;
    },
    async removeFile(payload: any) {
      if (payload && payload.type === SCREENSHOTS && this.thumbnails[payload.id]) {
        revokeThumbnailUrl(this.thumbnails, payload.id);
        Reflect.deleteProperty(this.thumbnails, payload.id);
      }

      await this.getDB();
      return new Promise((resolve, reject) => {
        const key = getKey(payload);
        const trans = this.db?.transaction([key], 'readwrite');
        const store = trans.objectStore(key);
        const request = store.delete(typeof payload === 'string' ? payload : payload.id);
        request.onsuccess = () => {
          resolve(true);
        };

        request.onerror = (e: string | undefined) => {
          reject(new Error(e));
        };
      });
    },
    async removeFiles(payload: { type: any }) {
      if (payload && payload.type === SCREENSHOTS) {
        this.thumbnails = [];
      }

      await this.getDB();

      const key = getKey(payload);
      return new Promise((resolve) => {
        const trans = this.db?.transaction([key], 'readwrite');
        const store = trans.objectStore(key);
        store.clear();
        trans.oncomplete = () => {
          resolve();
        };
      });
    },
    // Note: prefer using this function instead of the duplicates in the Upload component.
    async transformFilesToBase64(files: Blob[]) {
      return Promise.all(
        files.map(
          async (file: Blob) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              reader.onload = (fileResult) => {
                const fileBinaryData = fileResult.target.result;
                resolve({
                  name: file.name,
                  data: fileBinaryData,
                  format: file.name.split('.').pop(),
                });
              };

              reader.readAsDataURL(file);
            }),
        ),
      );
    },
  },
});
